import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Country, State } from '@vendasta/country-state';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LocationService } from '../../../../../../data-services/location.service';
import { DynamicOptionsComponent } from '../dynamic-options';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'automata-country-options',
  templateUrl: './location-options.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
  ],
})
export class LocationOptionsComponent implements OnInit, OnDestroy, DynamicOptionsComponent {
  destroyed$$ = new Subject<void>();

  countryOptionControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  countryOptions$ = this.locationService.getCountries();
  countrySelectOptions$: Observable<SelectInputOptionInterface[]>;
  selectedCountry$$ = new BehaviorSubject<string>(null);
  selectedCountry$ = this.selectedCountry$$.asObservable();
  countryZone$: Observable<string>;

  loadingStates = false;
  stateOptionControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  stateSelectOptions$: Observable<SelectInputOptionInterface[]>;

  cityControl: UntypedFormControl = new UntypedFormControl('');
  zipCodeControl: UntypedFormControl = new UntypedFormControl('');

  @Input() initialValue: string[];
  selectedValue$ = this.countryOptionControl.valueChanges;

  constructor(
    private locationService: LocationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const initialValue = this.initialValue || [];
    const countryValue = initialValue[0] || '';
    const stateValue = initialValue[1] || '';
    let cityValue = initialValue[2] || '';
    let zipCodeValue = initialValue[3] || '';
    if (cityValue === '*') {
      cityValue = '';
    }
    if (zipCodeValue === '*') {
      zipCodeValue = '';
    }

    this.countrySelectOptions$ = this.countryOptions$.pipe(
      take(1),
      map((opts) => opts.map(this.mapLocationToOption)),
      map((options) =>
        options.map((option) => {
          if (option.value === countryValue) {
            option.selected = true;
          }
          return option;
        }),
      ),
    );

    this.selectedCountry$$.next(countryValue);
    this.countryOptionControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        filter((value) => !!value),
        tap((value) => {
          if (this.selectedCountry$$.value !== value) {
            this.stateOptionControl.setValue('*');
            this.selectedCountry$$.next(value);
          }
        }),
      )
      .subscribe();

    this.countryZone$ = this.selectedCountry$.pipe(
      takeUntil(this.destroyed$$),
      switchMap((countryCode) => this.locationService.getCountryConfig(countryCode)),
      map((config) => config.labels?.zone),
    );

    this.stateSelectOptions$ = this.selectedCountry$.pipe(
      takeUntil(this.destroyed$$),
      tap((_) => {
        this.loadingStates = true;
        this.cdr.detectChanges();
      }),
      switchMap((selected) => this.locationService.getStates(selected)),
      map((opts) => opts.map(this.mapLocationToOption)),
      tap((options) => {
        if (!stateValue) {
          this.stateOptionControl.setValue('*');
          return;
        }
        options.map((option) => {
          if (option.value === stateValue) {
            option.selected = true;
          }
          return option;
        });
      }),
      tap((_) => {
        this.loadingStates = false;
        this.cdr.detectChanges();
      }),
    );

    // set initial values for the form controls
    this.countryOptionControl.setValue(countryValue);
    this.stateOptionControl.setValue(stateValue);
    this.cityControl.setValue(cityValue);
    this.zipCodeControl.setValue(zipCodeValue);
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  getSelectedValue(): string[] {
    const rules = [this.countryOptionControl?.value, this.stateOptionControl?.value];

    if (this.cityControl?.value) {
      rules.push(this.cityControl?.value);
    } else {
      rules.push('*');
    }

    if (this.zipCodeControl?.value) {
      rules.push(this.zipCodeControl?.value);
    } else {
      rules.push('*');
    }

    return rules;
  }

  mapLocationToOption(location: State | Country): SelectInputOptionInterface {
    return {
      label: location.name,
      value: location.code,
    };
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.countryOptionControl, this.stateOptionControl];
  }

  isFormValid(): boolean {
    return this.countryOptionControl.valid && this.stateOptionControl.valid;
  }
}
